import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import {
  LetterBlock,
  LetterCondition,
  LetterContent,
} from '../../api/LettersApi';

/**
 * One letter, as a list of paragraphs and lists.
 *
 * The editor never asks anyone to write a template. Where a letter varies — a sentence that depends
 * on knowing the court date, a paragraph that depends on whether the client has taken a driving
 * safety course — that is a dropdown, and the fields that fill themselves in are inserted from a
 * menu rather than typed.
 */

interface Props {
  content: LetterContent;
  /** Which fields may be inserted — a letter and the email are filled from different data. */
  placeholders: { name: string; label: string }[];
  /** Which conditions may be chosen, likewise. */
  conditions: { value: LetterCondition; label: string }[];
  readOnly: boolean;
  onChange: (content: LetterContent) => void;
  /** The email offers a ticket-details box; a letter has nothing of the kind. */
  allowDetailsBlock?: boolean;
}

function replaceBlock(
  content: LetterContent,
  index: number,
  block: LetterBlock,
): LetterContent {
  return {
    blocks: content.blocks.map((existing, i) => (i === index ? block : existing)),
  };
}

function moveBlock(content: LetterContent, index: number, delta: number): LetterContent {
  const target = index + delta;
  if (target < 0 || target >= content.blocks.length) return content;
  const blocks = [...content.blocks];
  const [moved] = blocks.splice(index, 1);
  blocks.splice(target, 0, moved);
  return { blocks };
}

const LetterBlockEditor: React.FC<Props> = ({
  content,
  placeholders,
  conditions,
  readOnly,
  onChange,
  allowDetailsBlock = false,
}) => {
  // Where an inserted field lands. Tracking the last focused box is what lets the "insert field"
  // chips put {{clientName}} where the person was typing rather than at the end of the letter.
  const focusRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const insertField = (name: string) => {
    const element = focusRef.current;
    if (!element) return;
    const token = `{{${name}}}`;
    const start = element.selectionStart ?? element.value.length;
    const end = element.selectionEnd ?? start;
    const next = element.value.slice(0, start) + token + element.value.slice(end);

    // React controlled inputs need the native setter to notice a programmatic change.
    const setter = Object.getOwnPropertyDescriptor(
      element instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype,
      'value',
    )?.set;
    setter?.call(element, next);
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.focus();
    const caret = start + token.length;
    window.requestAnimationFrame(() => element.setSelectionRange(caret, caret));
  };

  const conditionSelect = (
    label: string,
    value: LetterCondition | undefined,
    onSelect: (value: LetterCondition) => void,
  ) => (
    <FormControl size="small" sx={{ minWidth: 260 }} disabled={readOnly}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value ?? 'always'}
        onChange={(e) => onSelect(e.target.value as LetterCondition)}
      >
        {conditions.map((condition) => (
          <MenuItem key={condition.value} value={condition.value}>
            {condition.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      {!readOnly && (
        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Insert a field
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click into the text where you want it, then click a field. It is filled in per case
              when the letter is made.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
              {placeholders.map((placeholder) => (
                <Tooltip key={placeholder.name} title={placeholder.label}>
                  <Button
                    size="small"
                    variant="outlined"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insertField(placeholder.name)}
                  >
                    {placeholder.name}
                  </Button>
                </Tooltip>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2}>
        {content.blocks.map((block, index) => (
          <Card key={index} variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {block.type === 'list'
                    ? 'Numbered list'
                    : block.type === 'details'
                    ? 'Ticket details box'
                    : 'Paragraph'}{' '}
                  {index + 1}
                  {block.variant === 'closing' ? ' — sign-off' : ''}
                </Typography>
                {!readOnly && (
                  <Stack direction="row">
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => onChange(moveBlock(content, index, -1))}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === content.blocks.length - 1}
                      onClick={() => onChange(moveBlock(content, index, 1))}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() =>
                        onChange({
                          blocks: content.blocks.filter((_b, i) => i !== index),
                        })
                      }
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                )}
              </Stack>

              {block.type === 'details' ? (
                <Typography variant="body2" color="text.secondary">
                  The grey box listing the case's ticket number, citation, court date, city and
                  county. It fills itself in from the case and shows only the parts we know, so
                  there is nothing to write here — drag it up or down to move it, or delete it to
                  leave it out.
                </Typography>
              ) : block.type === 'paragraph' ? (
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    disabled={readOnly}
                    value={block.text ?? ''}
                    inputRef={(element) => element}
                    onFocus={(e) => {
                      focusRef.current = e.target as HTMLTextAreaElement;
                    }}
                    onChange={(e) =>
                      onChange(replaceBlock(content, index, { ...block, text: e.target.value }))
                    }
                  />

                  {conditionSelect('Include this paragraph', block.showWhen, (value) =>
                    onChange(replaceBlock(content, index, { ...block, showWhen: value })),
                  )}

                  {block.leadInText !== undefined ? (
                    <Box sx={{ pl: 2, borderLeft: 3, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        Opening sentence — joins the front of the paragraph above only when its
                        condition is met, so the rest of the paragraph survives without it.
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        size="small"
                        sx={{ mt: 1 }}
                        disabled={readOnly}
                        value={block.leadInText}
                        onFocus={(e) => {
                          focusRef.current = e.target as HTMLTextAreaElement;
                        }}
                        onChange={(e) =>
                          onChange(
                            replaceBlock(content, index, {
                              ...block,
                              leadInText: e.target.value,
                            }),
                          )
                        }
                      />
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        {conditionSelect('Include this sentence', block.leadInWhen, (value) =>
                          onChange(
                            replaceBlock(content, index, { ...block, leadInWhen: value }),
                          ),
                        )}
                        {!readOnly && (
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() =>
                              onChange(
                                replaceBlock(content, index, {
                                  ...block,
                                  leadInText: undefined,
                                  leadInWhen: undefined,
                                }),
                              )
                            }
                          >
                            Remove opening sentence
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  ) : (
                    !readOnly && (
                      <Box>
                        <Button
                          size="small"
                          onClick={() =>
                            onChange(
                              replaceBlock(content, index, {
                                ...block,
                                leadInText: '',
                                leadInWhen: 'court-date-known',
                              }),
                            )
                          }
                        >
                          Add a conditional opening sentence
                        </Button>
                      </Box>
                    )
                  )}
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  {(block.items ?? []).map((item, itemIndex) => (
                    <Box key={itemIndex}>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <Typography sx={{ mt: 1.2, minWidth: 24 }} color="text.secondary">
                          {itemIndex + 1}.
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          size="small"
                          disabled={readOnly}
                          value={item.text}
                          onFocus={(e) => {
                            focusRef.current = e.target as HTMLTextAreaElement;
                          }}
                          onChange={(e) =>
                            onChange(
                              replaceBlock(content, index, {
                                ...block,
                                items: (block.items ?? []).map((existing, i) =>
                                  i === itemIndex
                                    ? { ...existing, text: e.target.value }
                                    : existing,
                                ),
                              }),
                            )
                          }
                        />
                        {!readOnly && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              onChange(
                                replaceBlock(content, index, {
                                  ...block,
                                  items: (block.items ?? []).filter(
                                    (_item, i) => i !== itemIndex,
                                  ),
                                }),
                              )
                            }
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>

                      <TextField
                        fullWidth
                        multiline
                        size="small"
                        sx={{ mt: 1, pl: 4 }}
                        disabled={readOnly}
                        label="Sub-items, one per line"
                        placeholder="Leave empty for none"
                        value={(item.subItems ?? []).join('\n')}
                        onChange={(e) =>
                          onChange(
                            replaceBlock(content, index, {
                              ...block,
                              items: (block.items ?? []).map((existing, i) =>
                                i === itemIndex
                                  ? {
                                      ...existing,
                                      subItems: e.target.value
                                        .split('\n')
                                        .filter((line) => line.trim() !== ''),
                                    }
                                  : existing,
                              ),
                            }),
                          )
                        }
                      />
                    </Box>
                  ))}

                  {!readOnly && (
                    <Box>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          onChange(
                            replaceBlock(content, index, {
                              ...block,
                              items: [...(block.items ?? []), { text: '' }],
                            }),
                          )
                        }
                      >
                        Add item
                      </Button>
                    </Box>
                  )}

                  <Divider />
                  {conditionSelect('Include this list', block.showWhen, (value) =>
                    onChange(replaceBlock(content, index, { ...block, showWhen: value })),
                  )}
                </Stack>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      {!readOnly && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={() =>
              onChange({ blocks: [...content.blocks, { type: 'paragraph', text: '' }] })
            }
          >
            Add paragraph
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={() =>
              onChange({
                blocks: [
                  ...content.blocks,
                  { type: 'list', ordered: true, items: [{ text: '' }] },
                ],
              })
            }
          >
            Add numbered list
          </Button>
          {allowDetailsBlock && !content.blocks.some((block) => block.type === 'details') && (
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                onChange({ blocks: [...content.blocks, { type: 'details' }] })
              }
            >
              Add ticket details box
            </Button>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default LetterBlockEditor;
